import { useCallback, useRef } from 'preact/compat'

export type Size = { width: number; height: number }
export type OnResize = (size: Size) => void

interface UseResizeObserver {
  ref(el: HTMLElement | null): void
}

export const useResizeObserver = (onResize: OnResize): UseResizeObserver => {
  const onResizeRef = useRef<OnResize>()
  onResizeRef.current = onResize

  const ref = useRef<HTMLElement>(null)
  const observerRef = useRef<ResizeObserver>()

  const setRef = useCallback((el: HTMLElement | null): void => {
    if (!window.ResizeObserver || ref.current === el) {
      return
    }

    if (!observerRef.current) {
      const handleResize = ([entry]: ResizeObserverEntry[]) => {
        const { blockSize = 0, inlineSize = 0 } = entry?.borderBoxSize[0] ?? {}
        onResizeRef.current({ width: inlineSize, height: blockSize })
      }

      observerRef.current = new ResizeObserver(handleResize)
    }

    observerRef.current.disconnect()

    ref.current = el

    if (ref.current) {
      observerRef.current.observe(ref.current, { box: 'border-box' })
    }
  }, [])

  return { ref: setRef }
}
